class SizeInquiryButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.render();
    this.addEventListeners();
    this.initializeModal();
    this.maybeResumeAfterSiqCreate();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    const modal = document.querySelector('size-inquiry-form-modal');
    this.cleanupModalEventListeners(modal);
  }

  addEventListeners() {
    this.addEventListener('click', this.handleClick);
  }

  removeEventListeners() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    this.openSizeInquiryForm();
  }

  render() {
    const buttonText = this.textContent.trim() || "DON'T SEE YOUR SIZE?";
    this.innerHTML = '';
    this.textContent = buttonText;
    this.style.cursor = 'pointer';
    this.classList.add('size-inquiry-button');
  }

  initializeModal() {
    const modal = document.querySelector('size-inquiry-form-modal');
    if (!modal) return;
    
    // Check if modal is already globally initialized to prevent duplicate listeners
    if (!modal.dataset.listenersInitialized) {
      this.setupModalEventListeners();
      modal.dataset.listenersInitialized = 'true';
    }
  }

  setupModalEventListeners() {
    const modal = document.querySelector('size-inquiry-form-modal');
    if (!modal) return;

    // Remove existing listeners to prevent duplicates
    this.cleanupModalEventListeners(modal);

    const closeButton = modal.querySelector('.size-inquiry-form-modal__close-button');
    if (closeButton) {
      this.closeButtonHandler = () => this.hideSizeInquiryModal(modal);
      closeButton.addEventListener('click', this.closeButtonHandler);
    }

    const overlay = modal.querySelector('.size-inquiry-form-modal__overlay');
    if (overlay) {
      this.overlayHandler = () => this.hideSizeInquiryModal(modal);
      overlay.addEventListener('click', this.overlayHandler);
    }

    this.keydownHandler = (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        this.hideSizeInquiryModal(modal);
      }
    };
    document.addEventListener('keydown', this.keydownHandler);

    const form = modal.querySelector('#size-inquiry-form');
    if (form) {
      this.formSubmitHandler = (e) => this.handleSizeInquirySubmit(e, modal);
      form.addEventListener('submit', this.formSubmitHandler);
    }
  }

  cleanupModalEventListeners(modal) {
    if (!modal) return;

    const closeButton = modal.querySelector('.size-inquiry-form-modal__close-button');
    if (closeButton && this.closeButtonHandler) {
      closeButton.removeEventListener('click', this.closeButtonHandler);
    }

    const overlay = modal.querySelector('.size-inquiry-form-modal__overlay');
    if (overlay && this.overlayHandler) {
      overlay.removeEventListener('click', this.overlayHandler);
    }

    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }

    const form = modal.querySelector('#size-inquiry-form');
    if (form && this.formSubmitHandler) {
      form.removeEventListener('submit', this.formSubmitHandler);
    }
  }

  openSizeInquiryForm() {
    const productTitle = this.getAttribute('data-product-title');
    const productName = this.getAttribute('data-product-name');
    const productColor = this.getAttribute('data-product-color');
    const productId = this.getAttribute('data-product-id');
    const productImage = this.getAttribute('data-product-image');
    const productPrice = this.getAttribute('data-product-price');

    const quickAddDialog = this.closest('quick-add-dialog');
    if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
      quickAddDialog.closeDialog();
    }

    const currentUrl = window.location.href;
    const modal = document.querySelector('size-inquiry-form-modal');

    if (!modal) {
      console.error('Size inquiry modal not found');
      return;
    }

    this.updateSizeInquiryContent(modal, {
      title: productTitle,
      name: productName,
      color: productColor,
      url: currentUrl,
      id: productId,
      image: productImage,
      price: productPrice,
    });

    // Load product recommendations
    this.loadProductRecommendations(productId);

    // Setup slider arrows
    this.setupSliderArrows();

    this.showSizeInquiryModal(modal);
  }

  updateSizeInquiryContent(modal, productData) {
    const productImage = modal.querySelector('.size-inquiry-form-modal__product-image img');
    if (productImage && productData.image) {
      productImage.src = productData.image;
      productImage.alt = productData.name || 'Product Image';
    }

    const productTitle = modal.querySelector('.product-title');
    if (productTitle) {
      productTitle.textContent = productData.name || '';
    }

    const productColor = modal.querySelector('.product-color');
    if (productColor) {
      productColor.textContent = productData.color || '';
    }

    const productPrice = modal.querySelector('.product-price');
    if (productPrice) {
      productPrice.textContent = productData.price || '';
    }

    const modalTitle = modal.querySelector('.size-inquiry-form-modal__title');
    if (modalTitle) {
      modalTitle.textContent = `DON'T SEE YOUR SIZE?`;
    }

    const form = modal.querySelector('#size-inquiry-form');
    if (form) {
      const productTitleInput = form.querySelector('input[name="product_title"]');
      const productUrlInput = form.querySelector('input[name="product_url"]');
      const productIdInput = form.querySelector('input[name="product_id"]');
      const dateTimeInput = form.querySelector('input[name="date_time"]');

      if (productTitleInput) productTitleInput.value = productData.title || '';
      if (productUrlInput) productUrlInput.value = productData.url || '';
      if (productIdInput) productIdInput.value = productData.id || '';

      if (dateTimeInput) {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const formattedDateTime = `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
        dateTimeInput.value = formattedDateTime;
      }

      const requiredSizeInput = form.querySelector('#required_size');
      const customerEmailInput = form.querySelector('#customer_email');

      if (requiredSizeInput) requiredSizeInput.value = '';
      if (customerEmailInput) customerEmailInput.value = '';
    }
  }

  showSizeInquiryModal(modal) {
    modal.classList.add('active');
    const container = modal.querySelector('.size-inquiry-form-modal__container');
    if (container) {
      container.classList.remove('closing');
      container.classList.add('active');
    }
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const sizeInput = modal.querySelector('#required_size');
      if (sizeInput) sizeInput.focus();
    }, 100);
  }

  hideSizeInquiryModal(modal) {
    const container = modal.querySelector('.size-inquiry-form-modal__container');
    if (container) {
      container.classList.remove('active');
      container.classList.add('closing');
    }
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  handleSizeInquirySubmit(event, modal) {
    if (this.isSubmitting) {
      event.preventDefault();
      return;
    }

    const form = event.target;
    const customerName = (form.querySelector('#customer_name')?.value || '').trim();
    const requiredSize = form.querySelector('#required_size').value;
    const customerEmail = form.querySelector('#customer_email').value;

    if (!customerName || !requiredSize || !customerEmail) {
      event.preventDefault();
      this.showSizeInquiryError(form, 'Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      event.preventDefault();
      this.showSizeInquiryError(form, 'Please enter a valid email address');
      return;
    }

    // Validation passed — populate hidden customer fields, stash payload, close modal.
    // No event.preventDefault(): the form is {%- form 'create_customer' -%} and submits natively.
    const payload = this.buildSiqPayload(form);
    this.populateSiqCustomerFields(payload, form);
    sessionStorage.setItem('siqPendingPayload', JSON.stringify(payload));

    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');
    if (submitButton) {
      submitButton.setAttribute('data-text', 'Submitting...');
      if (submitButtonText) submitButtonText.textContent = 'Submitting...';
      submitButton.disabled = true;
    }

    this.isSubmitting = true;
    this.hideSizeInquiryModal(modal);
  }

  showSizeInquirySuccess(form, modal) {
    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');

    if (submitButton) {
      submitButton.setAttribute('data-text', 'Submitted');
      if (submitButtonText) {
        submitButtonText.textContent = 'Submitted';
      }
    }

    setTimeout(() => {
      this.hideSizeInquiryModal(modal);
      setTimeout(() => {
        if (submitButton) {
          const originalInnerText = submitButton.dataset.originalInnerText || 'Submit';
          const originalDataText = submitButton.dataset.originalDataText || 'Submit';
          
          submitButton.setAttribute('data-text', originalDataText);
          if (submitButtonText) {
            submitButtonText.textContent = originalDataText;
          }
          submitButton.disabled = false;
        }
      }, 300);
    }, 2000);
  }

  showSizeInquiryError(form, message = 'Error occurred') {
    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');
    
    if (submitButton) {
      const originalInnerText = submitButton.dataset.originalInnerText || 'Submit';
      const originalDataText = submitButton.dataset.originalDataText || 'Submit';
      
      submitButton.setAttribute('data-text', message);
      if (submitButtonText) {
        submitButtonText.textContent = message;
      }

      setTimeout(() => {
        submitButton.setAttribute('data-text', originalDataText);
        if (submitButtonText) {
          submitButtonText.textContent = originalDataText;
        }
        submitButton.disabled = false;
      }, 3000);
    }
  }

  get productTitle() {
    return this.getAttribute('data-product-title');
  }

  get productName() {
    return this.getAttribute('data-product-name');
  }

  get productColor() {
    return this.getAttribute('data-product-color');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get productImage() {
    return this.getAttribute('data-product-image');
  }

  get productPrice() {
    return this.getAttribute('data-product-price');
  }

  set productTitle(value) {
    this.setAttribute('data-product-title', value);
  }

  set productName(value) {
    this.setAttribute('data-product-name', value);
  }

  set productColor(value) {
    this.setAttribute('data-product-color', value);
  }

  set productId(value) {
    this.setAttribute('data-product-id', value);
  }

  set productImage(value) {
    this.setAttribute('data-product-image', value);
  }

  set productPrice(value) {
    this.setAttribute('data-product-price', value);
  }

  updateProductData(productData) {
    if (productData.title) this.productTitle = productData.title;
    if (productData.name) this.productName = productData.name;
    if (productData.color) this.productColor = productData.color;
    if (productData.id) this.productId = productData.id;
    if (productData.image) this.productImage = productData.image;
    if (productData.price) this.productPrice = productData.price;
  }

  loadProductRecommendations(productId) {
    if (!productId) return;

    const recommendationsWrapper = document.querySelector('#recommendations-swiper-wrapper');
    if (!recommendationsWrapper) return;

    // Show loading state
    recommendationsWrapper.innerHTML = '<div class="swiper-slide" style="width: 100%; text-align: center; padding: 20px; color: #9f9f9f; font-size: 12px;">Loading recommendations...</div>';

    // Fetch recommendations from Shopify's recommendation API
    fetch(`/recommendations/products.json?product_id=${productId}&limit=8`)
      .then(response => response.json())
      .then(data => {
        if (data.products && data.products.length > 0) {
          this.renderRecommendations(data.products, recommendationsWrapper);
          this.initializeSwiper();
        } else {
          recommendationsWrapper.innerHTML = '<div class="swiper-slide" style="width: 100%; text-align: center; padding: 20px; color: #9f9f9f; font-size: 12px;">No recommendations available</div>';
        }
      })
      .catch(error => {
        console.error('Error loading recommendations:', error);
        recommendationsWrapper.innerHTML = '';
      });
  }

  renderRecommendations(products, container) {
    container.innerHTML = '';

    // Show up to 8 products for slider
    const productsToShow = products.slice(0, 8);

    productsToShow.forEach(product => {
      // Create swiper-slide wrapper
      const swiperSlide = document.createElement('div');
      swiperSlide.className = 'swiper-slide';

      // Use a simple div instead of product-card to avoid ref errors
      const productCard = document.createElement('div');
      productCard.className = 'recommendation-item';

      // Product link for image
      const productLink = document.createElement('a');
      productLink.className = 'recommendation-link';
      productLink.href = `/products/${product.handle}`;

      const imageDiv = document.createElement('div');
      imageDiv.className = 'recommendation-image';

      const img = document.createElement('img');
      img.src = product.featured_image || '';
      img.alt = product.title || '';
      img.loading = 'lazy';

      imageDiv.appendChild(img);
      productLink.appendChild(imageDiv);
      productCard.appendChild(productLink);

      // Product details
      const detailsDiv = document.createElement('div');
      detailsDiv.className = 'recommendation-details';

      const titleDiv = document.createElement('div');
      titleDiv.className = 'recommendation-title';
      titleDiv.textContent = product.title || '';

      const priceDiv = document.createElement('div');
      priceDiv.className = 'recommendation-price';
      const price = product.price ? (product.price / 100).toFixed(2) : '0.00';
      priceDiv.textContent = `$${price}`;

      detailsDiv.appendChild(titleDiv);
      detailsDiv.appendChild(priceDiv);
      productCard.appendChild(detailsDiv);

      // Quick Add button - create directly with click handler
      const quickAddWrapper = document.createElement('div');
      quickAddWrapper.className = 'recommendation-quick-add-wrapper';

      const quickAddBtn = document.createElement('button');
      quickAddBtn.className = 'recommendation-quick-add';
      quickAddBtn.textContent = 'QUICK ADD';
      quickAddBtn.setAttribute('data-product-handle', product.handle);
      quickAddBtn.setAttribute('data-product-id', product.id);

      quickAddBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleQuickAdd(product);
      });

      quickAddWrapper.appendChild(quickAddBtn);
      productCard.appendChild(quickAddWrapper);
      swiperSlide.appendChild(productCard);
      container.appendChild(swiperSlide);
    });
  }

  async handleQuickAdd(product) {
    // Find the quick-add-dialog in the DOM
    const quickAddDialog = document.getElementById('quick-add-dialog');

    if (!quickAddDialog) {
      console.log('Quick add dialog not found, opening product page');
      window.location.href = `/products/${product.handle}`;
      return;
    }

    try {
      // Fetch the product page
      const productUrl = `/products/${product.handle}`;
      const response = await fetch(productUrl);

      if (!response.ok) {
        throw new Error('Failed to fetch product page');
      }

      const html = await response.text();
      const productHtml = new DOMParser().parseFromString(html, 'text/html');

      // Update the quick-add modal content using the proper selector
      const modalContent = document.getElementById('quick-add-modal-content');
      const productGrid = productHtml.querySelector('[data-product-grid-content]');

      if (modalContent && productGrid) {
        // Clear and update content
        modalContent.innerHTML = '';

        // Clone the node to get fresh scripts
        const clonedGrid = productGrid.cloneNode(true);
        modalContent.appendChild(clonedGrid);

        // Re-execute inline scripts
        const scripts = modalContent.querySelectorAll('script');
        scripts.forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode?.replaceChild(newScript, oldScript);
        });

        // Dispatch a custom event to trigger any necessary initializations
        setTimeout(() => {
          const event = new CustomEvent('quick-add:loaded', {
            detail: { productHandle: product.handle }
          });
          document.dispatchEvent(event);
        }, 100);
      }

      // Open the modal using showDialog method
      if (quickAddDialog.showDialog && typeof quickAddDialog.showDialog === 'function') {
        quickAddDialog.showDialog();
      } else {
        // Fallback: navigate to product page
        console.log('Quick add dialog method not found, opening product page');
        window.location.href = `/products/${product.handle}`;
      }
    } catch (error) {
      console.error('Error loading quick add:', error);
      // Fallback to product page
      window.location.href = `/products/${product.handle}`;
    }
  }

  initializeSwiper() {
    // Wait for Swiper to be available
    if (typeof Swiper === 'undefined') {
      console.error('Swiper is not loaded');
      return;
    }

    const swiperContainer = document.querySelector('#recommendations-container');
    const swiperWrapper = document.querySelector('#recommendations-swiper-wrapper');
    const prevButton = document.querySelector('#recommendations-prev');
    const nextButton = document.querySelector('#recommendations-next');
    const arrowsContainer = document.querySelector('.recommendations-arrows');

    if (!swiperContainer || !swiperWrapper) return;

    // Count the number of slides
    const slideCount = swiperWrapper.children.length;

    // Show/hide arrows based on slide count
    // Hide arrows if only 1 slide
    if (slideCount <= 1) {
      if (arrowsContainer) arrowsContainer.style.display = 'none';
    } else {
      if (arrowsContainer) arrowsContainer.style.display = 'flex';
    }

    // Destroy existing swiper instance if any
    if (swiperContainer.swiper) {
      swiperContainer.swiper.destroy(true, true);
    }

    // Initialize Swiper only if we have slides
    if (slideCount > 0) {
      new Swiper('#recommendations-container', {
        slidesPerView: 1.2,
        spaceBetween: 12,
        loop: slideCount > 1,
        navigation: slideCount > 1 ? {
          nextEl: '#recommendations-next',
          prevEl: '#recommendations-prev',
        } : false,
        breakpoints: {
          568: {
            slidesPerView: 1.3,
            spaceBetween: 12,
          },
          768: {
            slidesPerView: 1.5,
            spaceBetween: 12,
          },
        },
      });
    }
  }

  setupSliderArrows() {
    // Swiper handles arrows automatically, this method is no longer needed
    // but keeping it for compatibility
  }

  buildSiqPayload(form) {
    return {
      source: 'size-inquiry',
      name: (form.querySelector('#customer_name')?.value || '').trim(),
      email: (form.querySelector('#customer_email')?.value || '').trim().toLowerCase(),
      requiredSize: (form.querySelector('#required_size')?.value || '').trim(),
      productTitle: form.querySelector('input[name="product_title"]')?.value || '',
      productUrl: form.querySelector('input[name="product_url"]')?.value || '',
      productId: form.querySelector('input[name="product_id"]')?.value || '',
      dateTime: form.querySelector('input[name="date_time"]')?.value || '',
    };
  }

  populateSiqCustomerFields(payload, form) {
    const parts = payload.name.split(/\s+/).filter(Boolean);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ');
    const password = `Siq-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    const tags = ['size-inquiry', 'source-size-inquiry'];
    if (payload.requiredSize) tags.push(`size-${payload.requiredSize}`);

    const set = (id, val) => { const el = form.querySelector(`#${id}`); if (el) el.value = val; };
    set('siq-first-name', firstName);
    set('siq-last-name', lastName);
    set('siq-tags', tags.join(','));
    set('siq-password', password);
    set('siq-password-confirm', password);
  }

  postSiqToSheet(payload) {
    const GOOGLE_APPS_SCRIPT_URL =
      'https://script.google.com/macros/s/AKfycbxyqToaiC9uMN2mkRFKdZ9t-g1KePpf7jB_s6-uvPCAKKNaMNL-qeEKKtFZ9g8-hbSmtA/exec';
    return fetch(GOOGLE_APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        formId: 'form1',
        source: payload.source,
        productName: payload.productTitle,
        productURL: payload.productUrl,
        productID: payload.productId,
        dateTime: payload.dateTime,
        requiredSize: payload.requiredSize,
        email: payload.email,
        name: payload.name,
      }),
    });
  }

  maybeResumeAfterSiqCreate() {
    const raw = sessionStorage.getItem('siqPendingPayload');
    if (!raw) return;
    sessionStorage.removeItem('siqPendingPayload');
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    this.postSiqToSheet(payload)
      .then(() => this.showSiqSuccessToast())
      .catch((err) => console.warn('[size-inquiry] Sheet post failed on resume:', err));
  }

  showSiqSuccessToast() {
    const toast = document.createElement('div');
    toast.className = 'siq-toast';
    toast.textContent = "You're on the list! We'll notify you when your size is available.";
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4500);
  }
}

customElements.define('size-inquiry-button', SizeInquiryButton);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const modal = document.querySelector('size-inquiry-form-modal');
    if (modal && !modal.dataset.initialized) {
      modal.dataset.initialized = 'true';
    }
  });
} else {
  const modal = document.querySelector('size-inquiry-form-modal');
  if (modal && !modal.dataset.initialized) {
    modal.dataset.initialized = 'true';
  }
}